import type { ChangeEvent } from 'react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from 'hooks/useAuth';
import { isAvatarDataUrl } from 'helpers/user-identity';
import { UserAvatar } from '@/components/UserAvatar';
import { Button } from '@/components/ui/button';
import { Panel } from '../../ConsolePanel';
import { PHOTO_SIZE, photoToDataUrl } from './photo-data-url';

const ACCEPT = 'image/jpeg,image/png,image/webp';

const OUTLINE = 'border-line text-foreground hover:bg-hover h-8 rounded-lg border bg-transparent px-3 text-meta';

/**
 * Your photo, shown instead of your initials wherever your name is: the header, the team
 * lists, the experiment library and the activity log.
 *
 * Saved as soon as a file is chosen, like the badge colour: the badge on the left is the
 * preview, so a save button would only add a step. The file is cropped and resized in the
 * browser first, so what is stored is small whatever was chosen.
 */
export function ProfilePhoto() {
  const { user, updateUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  if (!user) return null;

  const save = async (avatar: string | null) => {
    try {
      await updateUser({ avatar });
    } catch (error) {
      console.error('Failed to save the profile photo', error);
      toast.error(
        avatar === null ? 'Your photo could not be removed. Please try again.' : 'Your photo could not be saved.',
      );
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await save(null);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Busy from the moment a file is chosen, not only while saving: decoding a large photo
   * takes a moment, and a second pick in that window would race two saves.
   */
  const onPick = async (ev: ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    // Reset so choosing the same file again still fires a change.
    ev.target.value = '';
    if (!file) return;

    setBusy(true);
    try {
      let avatar: string;
      try {
        avatar = await photoToDataUrl({ file });
      } catch (error) {
        console.error('Could not read the chosen photo', error);
        toast.error('That file could not be read as a photo. Choose a JPEG, PNG or WebP image.');
        return;
      }

      // The same check the server makes, so a refusal is explained here rather than as a 400.
      if (!isAvatarDataUrl(avatar)) {
        // Already 256px, so "smaller" would not help: only a very noisy image gets here.
        toast.error('That photo is too detailed to store. Try a simpler image.');
        return;
      }

      await save(avatar);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title="Photo"
      description="Shown instead of your initials wherever your name is. Cropped to a square."
      tourAnchor="settings-account-photo"
    >
      <div className="flex flex-wrap items-center gap-4">
        <UserAvatar user={user} size="lg" />

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          aria-label="Choose a photo"
          onChange={onPick}
          disabled={busy}
        />
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className={OUTLINE}
        >
          {user.avatar ? 'Change photo' : 'Upload photo'}
        </Button>

        {!!user.avatar && (
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={remove}
            className="text-dim hover:text-foreground hover:bg-hover h-8 rounded-lg px-3 text-meta"
          >
            Remove photo
          </Button>
        )}
      </div>

      <p className="supporting mt-3 mb-0">
        JPEG, PNG or WebP. It is resized to {PHOTO_SIZE} pixels before it is saved, so any size of file works.
      </p>
    </Panel>
  );
}
